import React, { useRef, useState } from 'react';
import { Icon } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import Button from 'react-bootstrap/Button';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import { MAX_BATCH_SIZE } from 'components/tasks/utils';
import { RangeSelectEvent } from 'utils/rangeSelection';
import { usePatchManyProjectsMutation } from 'services/projects/api';
import { sharedAssigneeId } from 'services/users/assignable';
import { Project } from 'services/projects/types';
import ProjectsList from './ProjectsList';
import { applyProjectSelection } from './utils';
import AddProjectButton from './AddProjectButton';
import ProjectsFilters, {
  DEFAULT_PROJECTS_FILTER_DRAFT,
  ProjectsFilterDraft,
} from './ProjectsFilters/ProjectsFilters';
import ListToolbar, { viewModeGroupClassName } from 'components/UI/ListToolbar';
import SelectionBar from 'components/UI/SelectionBar';

interface ProjectsViewProps {
  onAddProject?: (project: Partial<Project>) => void;
  onProjectClick: (projectId: string) => void;
  projects?: Project[] | null;
  showAddButton?: boolean;
  showCompletionFilter?: boolean;
  showDueDateFilter?: boolean;
  showViewModeButtons?: boolean;
}

const ProjectsView = ({
  onAddProject,
  onProjectClick,
  projects,
  showAddButton,
  showCompletionFilter,
  showDueDateFilter,
  showViewModeButtons,
}: ProjectsViewProps) => {
  const { t } = useTranslation();
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
  const lastSelectedProjectIdRef = useRef<string | undefined>(undefined);
  const [patchManyProjects] = usePatchManyProjectsMutation();
  const [filterDraft, setFilterDraft] = useState<ProjectsFilterDraft>(DEFAULT_PROJECTS_FILTER_DRAFT);
  const showCompleteProjects = filterDraft.completion === 'ALL' || filterDraft.completion === 'COMPLETE';
  const showIncompleteProjects = filterDraft.completion === 'ALL' || filterDraft.completion === 'INCOMPLETE';
  const showCancelledProjects = filterDraft.completion === 'ALL' || filterDraft.completion === 'CANCELLED';
  const showArchivedProjects = filterDraft.completion === 'ALL' || filterDraft.completion === 'ARCHIVED';

  const selectedProjectIds = selectedProjects
    .filter(selectedProject => !!selectedProject.id)
    .map(selectedProject => selectedProject.id);
  const isOverBatchLimit = selectedProjectIds.length > MAX_BATCH_SIZE;

  const assignSelectedProjectsToUser = (assigneeId: string | null) => {
    if (isOverBatchLimit) {
      return;
    }
    patchManyProjects({
      ids: selectedProjectIds,
      infos: { assignee: assigneeId },
    }).then(result => {
      if ('error' in result) {
        return;
      }
      setSelectedProjects(current => current.map(project => (
        selectedProjectIds.includes(project.id)
          ? { ...project, assignee: assigneeId }
          : project
      )));
    });
  };

  const handleProjectSelect = (
    project: Project,
    event?: RangeSelectEvent,
    displayedProjects: Project[] = [],
  ) => {
    const result = applyProjectSelection({
      displayedProjects,
      lastSelectedProjectId: lastSelectedProjectIdRef.current,
      project,
      selectedProjects,
      shiftKey: !!event?.shiftKey,
    });
    lastSelectedProjectIdRef.current = result.lastSelectedProjectId;
    setSelectedProjects(result.selectedProjects);
  };

  return (
    <>
      {
        (showAddButton || showCompletionFilter || showDueDateFilter || showViewModeButtons) && (
          <ListToolbar
            start={showViewModeButtons && (
              <div className={viewModeGroupClassName}>
                <Button
                  active
                  aria-label={t('listView')}
                  aria-pressed
                  onClick={() => undefined}
                  variant="outline-secondary"
                >
                  <Icon name="list" size="1x" />
                </Button>
              </div>
            )}
          >
            <ProjectsFilters
              onApply={setFilterDraft}
              showCompletionFilter={showCompletionFilter}
              showDueDateFilter={showDueDateFilter}
              value={filterDraft}
            />
            {
              showAddButton && (
                <AddProjectButton
                  onClick={onProjectClick}
                  style={{
                    lineHeight: '24px',
                  }}
                />
              )
            }
          </ListToolbar>
        )
      }
      <ProjectsList
        assignees={filterDraft.assignees}
        onAddProject={onAddProject}
        onProjectClick={onProjectClick}
        onProjectSelect={handleProjectSelect}
        onSelectAllProjects={projectsArray => {
          lastSelectedProjectIdRef.current = projectsArray[projectsArray.length - 1]?.id;
          setSelectedProjects(projectsArray);
        }}
        dueDatePreset={filterDraft.dueDate}
        projects={projects}
        selectedProjects={selectedProjects}
        showArchivedProjects={showArchivedProjects}
        showCancelledProjects={showCancelledProjects}
        showCompleteProjects={showCompleteProjects}
        showIncompleteProjects={showIncompleteProjects}
      />
      {
        selectedProjects.length > 0 && (
          <SelectionBar data-testid="projects-selection-bar">
            <Icon
              name="times"
              onClick={() => {
                lastSelectedProjectIdRef.current = undefined;
                setSelectedProjects([]);
              }}
              style={{
                cursor: 'pointer',
                marginRight: 10,
                width: 25,
              }}
            />
            <b>
              {t('xSelectedProjects', { count: selectedProjects.length })}
            </b>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: 8,
                marginTop: 8,
              }}
            >
              <div data-testid="batch-assignee-selector">
                <AssigneeButton
                  assigneeId={sharedAssigneeId(selectedProjects)}
                  onChange={assignSelectedProjectsToUser}
                />
              </div>
            </div>
            {
              isOverBatchLimit && (
                <div role="alert" style={{ marginTop: 8 }}>
                  {t('selectAtMostXItems', { count: MAX_BATCH_SIZE })}
                </div>
              )
            }
          </SelectionBar>
        )
      }
    </>
  );
};

export default ProjectsView;
